/*
 * Listening settings and the listening position. Shared byte for byte between
 * know-web and know-mobile. Listening is off until the reader turns it on.
 */

import { DEFAULT_READ_RULES, type ReadRules } from './script';

export type HighlightMode = 'follow' | 'sentence' | 'off';
export type SleepTimer = 'off' | 'section' | 'document' | 15 | 30 | 45 | 60;

export interface Pronunciation {
  /** The word as written. */
  text: string;
  /** How to say it, as a plain respelling ("fray nel"). */
  say: string;
}

export interface ListenSettings {
  /** Off by default: no Listen control, ribbon or rail voice appears until this is on. */
  enabled: boolean;
  voice: string;
  /** 0.8 to 2. */
  rate: number;
  /** When true, `rates[voice]` overrides `rate` for that voice. */
  ratePerVoice: boolean;
  rates: Record<string, number>;
  highlight: HighlightMode;
  /** Keep the spoken line in view while playing; any scroll releases it. */
  follow: boolean;
  read: ReadRules;
  pauseBetweenSectionsMs: number;
  /** What back and forward step by in the app. */
  step: 'sentence' | 'paragraph';
  /** What the lock screen's and headphones' skip buttons do. */
  lockScreenSkip: 'section' | '15s';
  stopAfter: 'document' | 'section';
  sleep: SleepTimer;
  pronunciations: Pronunciation[];
  /** iOS: download the voice only on Wi-Fi. */
  wifiOnly: boolean;
}

export const DEFAULT_LISTEN_SETTINGS: ListenSettings = {
  enabled: false,
  voice: 'af_heart',
  rate: 1,
  ratePerVoice: false,
  rates: {},
  highlight: 'follow',
  follow: true,
  read: DEFAULT_READ_RULES,
  pauseBetweenSectionsMs: 800,
  step: 'sentence',
  lockScreenSkip: 'section',
  stopAfter: 'document',
  sleep: 'off',
  pronunciations: [],
  wifiOnly: true,
};

export const RATES = [0.8, 1, 1.1, 1.25, 1.5, 1.75, 2] as const;

/** Storage key; the web appends `:<sub>` so each signed-in reader keeps their own. */
export const LISTEN_SETTINGS_KEY = 'knowsh.listen.settings';
export const LISTEN_POSITIONS_KEY = 'knowsh.listen.positions';

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const oneOf = <T extends string | number>(v: unknown, allowed: readonly T[], d: T): T => (allowed.includes(v as T) ? (v as T) : d);
const num = (v: unknown, lo: number, hi: number, d: number) => (typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi ? v : d);
const bool = (v: unknown, d: boolean) => (typeof v === 'boolean' ? v : d);

/** Read saved settings defensively: anything missing or malformed falls back to the default. */
export function parseListenSettings(saved: unknown): ListenSettings {
  const d = DEFAULT_LISTEN_SETTINGS;
  if (!isObj(saved)) return { ...d, read: { ...d.read }, rates: {}, pronunciations: [] };
  const r = isObj(saved.read) ? saved.read : {};
  const rates: Record<string, number> = {};
  if (isObj(saved.rates)) for (const [k, v] of Object.entries(saved.rates)) if (typeof v === 'number' && v >= 0.5 && v <= 3) rates[k] = v;
  const pronunciations = Array.isArray(saved.pronunciations)
    ? saved.pronunciations.filter((p): p is Pronunciation => isObj(p) && typeof p.text === 'string' && typeof p.say === 'string' && p.text.trim() !== '')
    : [];
  return {
    enabled: bool(saved.enabled, d.enabled),
    voice: typeof saved.voice === 'string' && saved.voice ? saved.voice : d.voice,
    rate: num(saved.rate, 0.5, 3, d.rate),
    ratePerVoice: bool(saved.ratePerVoice, d.ratePerVoice),
    rates,
    highlight: oneOf(saved.highlight, ['follow', 'sentence', 'off'] as const, d.highlight),
    follow: bool(saved.follow, d.follow),
    read: {
      numbers: bool(r.numbers, d.read.numbers),
      categories: bool(r.categories, d.read.categories),
      code: oneOf(r.code, ['skip', 'announce', 'read'] as const, d.read.code),
      tables: oneOf(r.tables, ['summary', 'rows'] as const, d.read.tables),
      quotes: oneOf(r.quotes, ['attribute', 'text'] as const, d.read.quotes),
      elements: oneOf(r.elements, ['name', 'words', 'skip'] as const, d.read.elements),
    },
    pauseBetweenSectionsMs: num(saved.pauseBetweenSectionsMs, 0, 3000, d.pauseBetweenSectionsMs),
    step: oneOf(saved.step, ['sentence', 'paragraph'] as const, d.step),
    lockScreenSkip: oneOf(saved.lockScreenSkip, ['section', '15s'] as const, d.lockScreenSkip),
    stopAfter: oneOf(saved.stopAfter, ['document', 'section'] as const, d.stopAfter),
    sleep: oneOf(saved.sleep, ['off', 'section', 'document', 15, 30, 45, 60] as const, d.sleep),
    pronunciations,
    wifiOnly: bool(saved.wifiOnly, d.wifiOnly),
  };
}

/** The rate to play at for a voice. */
export const rateFor = (s: ListenSettings, voice = s.voice): number => (s.ratePerVoice ? s.rates[voice] ?? s.rate : s.rate);

/** The reader's pronunciations as a lexicon for buildScript. */
export const lexiconOf = (s: ListenSettings): Record<string, string> =>
  Object.fromEntries(s.pronunciations.map(p => [p.text.trim(), p.say.trim()]));

/**
 * Where the voice was, as a text anchor rather than a time: time changes with voice,
 * speed and every edit; a block and an offset survive all three.
 */
export interface ListenPosition {
  docId: string;
  blockId: string;
  charOffset: number;
  voice: string;
  /** ISO time. */
  updatedAt: string;
}

export function parsePositions(saved: unknown): Record<string, ListenPosition> {
  if (!isObj(saved)) return {};
  const out: Record<string, ListenPosition> = {};
  for (const [docId, p] of Object.entries(saved)) {
    if (isObj(p) && typeof p.blockId === 'string' && typeof p.charOffset === 'number' && typeof p.updatedAt === 'string') {
      out[docId] = { docId, blockId: p.blockId, charOffset: p.charOffset, voice: typeof p.voice === 'string' ? p.voice : DEFAULT_LISTEN_SETTINGS.voice, updatedAt: p.updatedAt };
    }
  }
  return out;
}
